import React, { Suspense, ReactNode } from 'react';
import { Navigate } from 'react-router';

import LoginPage from 'components/auth/Login/LoginPage';
import { useRouterLocation, useSelectorActiveChain } from 'components/hooks';
import { devConfig } from 'config';
import {
  Chains,
  chainsMap,
} from 'reduxState/exchange/exchangeActiveChainUtils';
import { useSelector } from 'reduxState/store';

import ErrorPage from './ErrorPage';

const PLACEHOLDER_DIV = <div style={{ padding: '100px 20px' }} />;

type Props = {
  enforceLogged?: boolean;
  enforceWallet?: boolean;
  enforceMinTier?: number;
  enforceTier?: number;
  enforceChain?: Chains;
  enforceWalletChainId?: Chains;
  children: ReactNode;
};

const LayoutContentEnforcer = ({
  enforceLogged,
  enforceWallet,
  enforceMinTier,
  enforceTier,
  enforceChain,
  children,
}: Props): JSX.Element | null => {
  const location = useRouterLocation();
  const userIntialized = useSelector(state => state.currentUser.initialized);
  // const userPendingKycStatus = useSelector(
  //   state => state.currentUser.pendingKycStatus
  // );
  const userLoggedIn = useSelector(state => state.currentUser.loggedIn);
  const isCatchAllUser = useSelector(state => state.currentUser.isCatchAllUser);
  // const userKycTier = useSelector(state => state.currentUser.kycTier);
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const { activeChain } = useSelectorActiveChain();

  if (enforceChain) {
    if (!activeChain) {
      return PLACEHOLDER_DIV;
    }
    if (activeChain !== enforceChain) {
      return (
        <ErrorPage
          title={`Restricted to ${chainsMap[enforceChain]?.name}`}
          text={`You are currently using ${chainsMap[activeChain]?.name} network. To access this page switch to ${chainsMap[enforceChain]?.name}.`}
        />
      );
    }
  }

  if (enforceLogged) {
    if (!userIntialized) {
      return PLACEHOLDER_DIV;
    }
    if (!userLoggedIn || isCatchAllUser) {
      console.log('[LayoutEnforce] Redirecting to login');
      return <Navigate to="/wallet/api-keys/login" />;
    }
  }

  if (enforceWallet && !walletAddress) {
    console.log('[LayoutEnforce] Redirecting to unlock');
    return (
      <Navigate
        to={
          location.pathname.includes('unlock')
            ? '/unlock'
            : `/unlock?afterUnlock=${location.pathname + location.search}`
        }
      />
    );
  }

  if (enforceMinTier) {
    if (!userIntialized) {
      return PLACEHOLDER_DIV;
    }
    if (!userLoggedIn) {
      console.log('[LayoutEnforce] Redirecting to login (tier_enforce)');
      return <Navigate to="/wallet/api-keys/login" />;
    }
    // if (userKycTier < enforceMinTier) {
    //   return enforceMinTier === 1 ? (
    //     <Navigate to="/wallet/api-keys/verify" />
    //   ) : (
    //     <AccountDetailsPage />
    //   );
    // }
  }

  if (enforceTier !== undefined) {
    if (!userIntialized) {
      return PLACEHOLDER_DIV;
    }

    if (userLoggedIn) {
      // Enforcement for tier, disabled states
      // eg.user is tier 0 and enter tier 2 url - redirect to Account page
      // eg.user is tier 2 and open tier 1 form - Account page
      // if (
      //   userPendingKycStatus === tierStatusMap.BANNED ||
      //   ((enforceTier || enforceTier === 0) && enforceTier !== userKycTier)
      // ) {
      //   return <AccountDetailsPage />;
      // }

      return <>{children}</>;
    }

    if (devConfig.disabledKycEnforcer) {
      console.warn('DEV MODE: disabledKycEnforcer');
      return <>{children}</>;
    }

    return <LoginPage disabledRedirect />;
  }

  return <>{children}</>;
};

type LayoutProps = {
  enforceLogged?: boolean;
  enforceWallet?: boolean;
  enforceMinTier?: number;
  enforceTier?: number;
  enforceChain?: Chains;
  children?: ReactNode;
};

export function EnforcedLayout({
  enforceLogged,
  enforceWallet,
  enforceMinTier,
  enforceTier,
  enforceChain,
  children,
}: LayoutProps) {
  return (
    <Suspense fallback={<div />}>
      <LayoutContentEnforcer
        enforceLogged={enforceLogged}
        enforceWallet={enforceWallet}
        enforceMinTier={enforceMinTier}
        enforceTier={enforceTier}
        enforceChain={enforceChain}
      >
        {children}
      </LayoutContentEnforcer>
    </Suspense>
  );
}

// export function WalletEnforcedLayout(props: LayoutProps) {
//   return <EnforcedLayout enforceWallet {...props} />;
// }

export const BaseLayout = ({ children }: { children: ReactNode }) => {
  return <Suspense fallback={<div />}>{children}</Suspense>;
};
